import { Popper, PopperPlacementType } from "@mui/base/Popper";
import useMuiTheme from "@mui/material/styles/useTheme";
import { useAnimateHeight } from "@redotech/react-animation/transition";
import { useEventListener } from "@redotech/react-util/event";
import { useSize } from "@redotech/react-util/size";
import classnames from "classnames";
import {
  ForwardedRef,
  forwardRef,
  memo,
  ReactNode,
  UIEventHandler,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useDebounce } from "usehooks-ts";
import * as dropdownCss from "./dropdown.module.css";
import { findFocusTrapContainer } from "./find-focus-trap-container";
import { Flex, FlexProps } from "./flex";
import * as darkModeCss from "./theme/component-colors-dark.module.css";
import { Tooltip } from "./tooltip/tooltip";

export const Dropdown = memo(
  forwardRef(function Dropdown(
    {
      anchor,
      children,
      open,
      fitToAnchor = true,
      flip = true,
      placement = undefined,
      darkMode = false,
      constrainHeight = true,
      flexProps,
      className,
      onScroll,
    }: {
      anchor: HTMLElement | null;
      children: ReactNode | ReactNode[];
      open: boolean;
      fitToAnchor?: boolean;
      flip?: boolean;
      placement?: PopperPlacementType;
      darkMode?: boolean;
      constrainHeight?: boolean;
      flexProps?: FlexProps;
      className?: string;
      onScroll?: UIEventHandler<HTMLDivElement>;
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const muiTheme = useMuiTheme();
    const [dropdown, setDropdown] = useState<HTMLElement | null>(null);

    const [animateStyles, animating] = useAnimateHeight(dropdown);

    const debounceOpen = useDebounce(open, 1);

    const size = useSize(anchor);

    const [windowHeight, setWindowHeight] = useState(() => window.innerHeight);
    useEventListener(window, "resize", () =>
      setWindowHeight(window.innerHeight),
    );

    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
    useLayoutEffect(() => {
      if (!open || !anchor || !constrainHeight) {
        setMaxHeight(undefined);
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const gapPx = 24;
      const availableBelow = windowHeight - rect.bottom - gapPx;
      const availableAbove = rect.top - gapPx;
      const available = flip
        ? Math.max(availableBelow, availableAbove)
        : availableBelow;
      setMaxHeight(Math.min(415, Math.max(available, 0)));
    }, [open, anchor, constrainHeight, flip, windowHeight]);

    const width = fitToAnchor ? size && `${size.width}px` : undefined;

    // Render the popper inside the focus-trapped modal element when the anchor
    // is inside one, so the modal's focus enforcement does not yank focus out
    // of inputs the user types into (e.g. a dropdown search box).
    const themeDefaultContainer =
      muiTheme.components?.MuiPopper?.defaultProps?.container ?? undefined;
    const popperContainer = useMemo(
      () => findFocusTrapContainer(anchor) ?? themeDefaultContainer,
      [anchor, themeDefaultContainer],
    );

    return (
      <Popper
        anchorEl={anchor}
        className={dropdownCss.root}
        container={popperContainer}
        modifiers={[{ name: "flip", enabled: flip }]}
        open={open || debounceOpen || animating}
        placement={placement} // Avoid cursed type check error when trying to import the PopupPlacement type
        ref={ref}
      >
        <Flex
          borderColor="primary"
          borderStyle="solid"
          borderWidth="1px"
          className={classnames(
            dropdownCss.dropdown,
            darkMode && darkModeCss.darkMode,
            {
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [dropdownCss.constrainHeight]: constrainHeight,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [dropdownCss.leave]: !open || !debounceOpen,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [dropdownCss.animating]: animating,
            },
            className,
          )}
          data-redo-dropdown
          dir="column"
          id="dropdown"
          onScroll={onScroll}
          p="md"
          radius="md"
          ref={setDropdown}
          style={{ ...animateStyles, width, maxHeight: maxHeight ?? undefined }}
          {...flexProps}
        >
          {(animating || (open && debounceOpen)) && children}
        </Flex>
      </Popper>
    );
  }),
);

export const DropdownOption = memo<{
  action?: () => void;
  children: ReactNode | ReactNode[];
  fontFamily?: string;
  disabled?: boolean;
  stopPropagation?: boolean;
  darkMode?: boolean;
  tooltip?: string;
}>(function DropdownOption({
  action,
  children,
  fontFamily,
  disabled,
  stopPropagation,
  darkMode,
  tooltip,
}) {
  return tooltip ? (
    <Tooltip arrow title={tooltip}>
      <button
        className={classnames(dropdownCss.option, {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [dropdownCss.dark]: darkMode,
        })}
        disabled={disabled}
        onClick={(event) => {
          if (stopPropagation) {
            event.stopPropagation();
          }
          action?.();
        }}
        style={{ fontFamily }}
        type="button"
      >
        {children}
      </button>
    </Tooltip>
  ) : (
    <button
      className={classnames(dropdownCss.option, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [dropdownCss.dark]: darkMode,
      })}
      disabled={disabled}
      onClick={(event) => {
        if (stopPropagation) {
          event.stopPropagation();
        }
        action?.();
      }}
      style={{ fontFamily }}
      type="button"
    >
      {children}
    </button>
  );
});
